import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Loader } from 'components/Loader';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { TotemSelect } from 'components/TotemSelect';
import { TotemCheckbox } from 'components/TotemCheckbox';
import { toast } from 'react-toastify';

import {
    useGetProductsByCategoryQuery,
    useRemoveProductInOrdersMutation,
    useReplaceableProductOrdersAndFutureStockVariationsQuery,
    useReplaceProductInOrdersMutation,
} from 'data/__generated__';

export const ProductModal = ({
    productId,
    displayKg,
    fullname,
    categoryId,
    portion,
}: {
    productId: string;
    displayKg: boolean;
    fullname: string;
    categoryId: string;
    portion: number;
}) => {
    const [replacementProduct, setReplacementproduct] = useState<{
        _id: string;
        fullname: string;
        portion: number;
    } | null>();
    const [shouldSendEmail, setShouldSendEmail] = useState(true);

    const {
        loading: productFromSameCategoryLoading,
        data: productFromSameCategory,
        error: productFromSameCategoryError,
    } = useGetProductsByCategoryQuery({ variables: { categoryId } });

    const {
        loading: futureStockVariationsAndOrdersLoading,
        data: futureStockVariationsAndOrdersData,
        error: futureStockVariationsAndOrdersError,
        refetch: refetchOrders,
    } = useReplaceableProductOrdersAndFutureStockVariationsQuery({
        variables: {
            productId,
        },
    });

    const [removeProductInOrders, { loading: removeProductInOrdersLoading }] = useRemoveProductInOrdersMutation();

    const [replaceProductInOrders, { loading: replaceProductInOrdersLoading }] = useReplaceProductInOrdersMutation();

    if (productFromSameCategoryLoading || futureStockVariationsAndOrdersLoading) {
        return <Loader />;
    }

    if (
        productFromSameCategoryError ||
        !productFromSameCategory ||
        futureStockVariationsAndOrdersError ||
        !futureStockVariationsAndOrdersData
    ) {
        throw new Error('Une erreur est survenue lors de la récupération des produits');
    }

    const replaceProductInOrdersHandler = async () => {
        if (!replacementProduct) {
            return;
        }

        const { data } = await replaceProductInOrders({
            variables: {
                productToRemoveId: productId,
                productToAddId: replacementProduct._id,
                shouldSendEmail,
            },
        });

        const errors = data?.replaceProductInOrders.errors;

        if (errors?.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>{error}</span>, { autoClose: false });
            });

            return;
        }

        toast.success(
            `Le produit "${fullname}" a bien été remplacé dans les commandes par "${replacementProduct.fullname}"!`,
        );
        await refetchOrders();
    };

    const removeProductInOrdersHandler = async () => {
        const { data } = await removeProductInOrders({
            variables: {
                productToRemoveId: productId,
                shouldSendEmail,
            },
        });

        const errors = data?.removeProductInOrders.errors;

        if (errors?.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>{error}</span>, { autoClose: false });
            });

            return;
        }

        toast.success(`Le produit "${fullname}" a bien été supprimé des commandes!`);
        await refetchOrders();
    };

    const productsOptions =
        productFromSameCategory?.getProductsByCategory?.map((product) => {
            return {
                value: product._id,
                label: `${product.fullname}, stock non reservé: ${product.availableStock} ${displayKg ? 'kg' : 'unités'}`,
            };
        }) || [];

    const {
        replaceableProductOrdersAndFutureStockVariations: { orders, futureStockVariations },
    } = futureStockVariationsAndOrdersData;

    const modifiableOrderFutureQuantities = futureStockVariations.reduce((acc, { delta }) => (acc += delta), 0);
    const sortedPreparationDates = futureStockVariations.map(({ useDate }) => useDate).sort();
    const closestDateQuantityToPrepare = futureStockVariations
        .filter(({ useDate }) => useDate === sortedPreparationDates[0])
        .reduce((acc, { delta }) => (acc += Math.abs(delta)), 0);

    return (
        <ActionsContainer>
            <ModalTitle>Souhaitez-vous remplacer ou supprimer ?</ModalTitle>
            <ModalText>
                Vous pouvez choisir de remplacer ce produit par un autre produit de la même catégorie, ou bien de
                supprimer ce produit des commandes, les portions ne seront pas réallouées.
            </ModalText>
            <ModalText>Ce choix impactera toutes les commandes modifiables ci-dessous :</ModalText>
            <OrdersContainer>
                {orders.map(({ _id: orderId, date_delivery, organization, totem, number, products }) => {
                    const product = products.find(({ _id }) => _id === productId);
                    return (
                        <li key={orderId}>
                            <b>{date_delivery}</b> -{' '}
                            <Link to={`/order/${orderId}`} target="_blank">
                                {organization.name} - Totem N°{totem.number}: Commande N°{number}
                            </Link>
                            , quantité: {product?.quantity ?? 0}
                        </li>
                    );
                })}
            </OrdersContainer>
            <ModalText>
                💡 Il y a <b>{Math.abs(modifiableOrderFutureQuantities || 0)}</b>
                {displayKg ? ' kg ' : ' unités '} de produit confirmés (qui devraient être preparés) en total. Parmi eux{' '}
                <b>{closestDateQuantityToPrepare}</b>
                {displayKg ? ' kg ' : ' unités '} devrait être préparés le {sortedPreparationDates[0]}. Veuillez vous
                assurer que les quantités suffisantes de produit vont être commandées/ livrées avant cette date si
                possible afin d'éviter un soldout
            </ModalText>
            <TotemSelect
                label={`Remplacer ${fullname} par :`}
                placeholder="Sélectionner le produit de remplacement"
                options={productsOptions}
                value={replacementProduct?._id ?? null}
                onChange={(value) => {
                    const product = productFromSameCategory?.getProductsByCategory.find(({ _id }) => _id === value);
                    setReplacementproduct(
                        product
                            ? {
                                  _id: product._id,
                                  fullname: product.fullname,
                                  portion: product.portion,
                              }
                            : null,
                    );
                }}
            />
            {replacementProduct ? (
                <ModalText>
                    {`Vous allez remplacer avec le ratio suivant : ${replacementProduct.portion} produits (${fullname}) donneront ${portion} produits (${replacementProduct.fullname})`}
                    {replacementProduct && portion !== replacementProduct.portion ? (
                        <WarningText>
                            ⚠️🚨 Attention : le ratio de remplacement est différent du ratio du produit de remplacement.
                            ⚠️🚨
                        </WarningText>
                    ) : null}
                </ModalText>
            ) : null}
            <TotemCheckboxContainer>
                <TotemCheckbox
                    label="Notifier le client par mail"
                    checked={shouldSendEmail}
                    onChange={() => setShouldSendEmail(!shouldSendEmail)}
                />
            </TotemCheckboxContainer>

            <TotemPrimaryLoadingButton
                loading={replaceProductInOrdersLoading}
                useIcon={false}
                label="Remplacer dans toutes les commandes"
                disabled={!replacementProduct?._id}
                type="button"
                onClick={replaceProductInOrdersHandler}
            />
            <ModalText>OU</ModalText>
            <TotemPrimaryLoadingButton
                loading={removeProductInOrdersLoading}
                useIcon={false}
                label="Supprimer de toutes les commandes"
                onClick={async (e) => {
                    e.preventDefault();
                    await removeProductInOrdersHandler();
                }}
                type="button"
            />
        </ActionsContainer>
    );
};

const ModalTitle = styled.div`
    padding: 10px 0;
    color: ${({ theme }) => theme.textColor};
    font-weight: bold;
    font-size: 1.8em;
`;

const ModalText = styled.div`
    padding: 10px 0;
`;

const OrdersContainer = styled.div`
    max-height: 300px;
    overflow-y: scroll;

    ::-webkit-scrollbar {
        width: 5px;
        height: 0;
    }
    ::-webkit-scrollbar-thumb {
        border-radius: ${({ theme }) => theme.borderRadius};
        background-color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const ActionsContainer = styled.div`
    margin-top: 5px;
`;

const WarningText = styled.div`
    color: ${({ theme }) => theme.warningColor};
    font-weight: 800;
`;

const TotemCheckboxContainer = styled.div`
    margin: 10px 0;
`;
